var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback } from 'react';
import { useListContext } from 'ra-core';
import { styled, useThemeProps, } from '@mui/material/styles';
import { Button } from './Button';
/**
 * Select all items in the current List context.
 *
 * Used in Datagrid's bulk action toolbar.
 *
 * @typedef {Object} Props the props you can use
 * @prop {string} label Button label. Defaults to 'ra.action.select_all_button', translated.
 * @prop {string} limit Maximum number of items to select. Defaults to 250.
 * @prop {function} queryOptions Object of options passed to react-query.
 *
 * @param {Props} props
 *
 * @example
 *
 * import { List, Datagrid, BulkActionsToolbar, SelectAllButton, BulkDeleteButton } from 'react-admin';
 *
 * const PostSelectAllButton = () => (
 *     <SelectAllButton
 *         label="Select all records"
 *         queryOptions={{ meta: { foo: 'bar' } }}
 *     />
 * );
 *
 * export const PostList = () => (
 *     <List>
 *         <Datagrid
 *             bulkActionsToolbar={
 *                 <BulkActionsToolbar selectAllButton={PostSelectAllButton}>
 *                     <BulkDeleteButton />
 *                 </BulkActionsToolbar>
 *             }
 *         >
 *             ...
 *         </Datagrid>
 *     </List>
 * );
 */
export var SelectAllButton = function (inProps) {
    var props = useThemeProps({
        props: inProps,
        name: PREFIX,
    });
    var _a = props.label, label = _a === void 0 ? 'ra.action.select_all_button' : _a, _b = props.limit, limit = _b === void 0 ? 250 : _b, onClick = props.onClick, queryOptions = props.queryOptions, rest = __rest(props, ["label", "limit", "onClick", "queryOptions"]);
    var _c = useListContext(), total = _c.total, selectedIds = _c.selectedIds, onSelectAll = _c.onSelectAll, data = _c.data;
    var handleClick = useCallback(function (event) {
        onSelectAll({ limit: limit, queryOptions: queryOptions });
        if (onClick)
            onClick(event);
    }, [onClick, onSelectAll, queryOptions, limit]);
    var areAllDataSelected = data && data.every(function (item) { return selectedIds.includes(item.id); });
    if (total === selectedIds.length ||
        selectedIds.length >= limit ||
        !areAllDataSelected)
        return null;
    return (React.createElement(StyledButton, __assign({ label: label, onClick: handleClick, type: "button" }, rest)));
};
var PREFIX = 'RaSelectAllButton';
var StyledButton = styled(Button, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var theme = _a.theme;
    return ({
        '&:hover': {
            backgroundColor: "color-mix(in srgb, ".concat((theme.vars || theme).palette.primary.main, ", transparent 12%)"),
            // Reset on mouse devices
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
    });
});
//# sourceMappingURL=SelectAllButton.js.map